import React, { useEffect, useMemo, useState } from "react";
import { formatDate } from "../utils/date";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ROLES, ROLE_LABELS } from "../lib/rbac";
import { useNavigate } from "react-router-dom";
import { rowOpenProps } from "../hooks/useRowOpen";
import DetailDialog from "../components/DetailDialog";
import DateInput from "../components/DateInput";
import { AlertTriangle, Wrench, ShieldOff } from "lucide-react";

const EFFECT_BADGE = {
    role: { label: "Allow (role)", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    override_allow: { label: "Allow (override)", className: "bg-sky-50 text-sky-700 border-sky-200" },
    override_deny: { label: "Denied (override)", className: "bg-rose-50 text-rose-700 border-rose-200" },
    not_granted: { label: "Not granted", className: "bg-gray-100 text-gray-500 border-gray-200" },
};

function formatExp(iso) {
    if (!iso) return "Never";
    try { return formatDate(iso); } catch { return iso; }
}

export default function AdminPermissions() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [detail, setDetail] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [modules, setModules] = useState([]);
    const [grants, setGrants] = useState([]);
    const [effective, setEffective] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openGrant, setOpenGrant] = useState(false);
    const [groupFilter, setGroupFilter] = useState("");
    const [effSearch, setEffSearch] = useState("");

    // Grant form state
    const [gKey, setGKey] = useState("");
    const [gEffect, setGEffect] = useState("allow");
    const [gScope, setGScope] = useState("global");
    const [gModule, setGModule] = useState("");
    const [gExpires, setGExpires] = useState("");
    const [gReason, setGReason] = useState("");

    // Iter14 — Reconcile-conflicts state.
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [reconcileData, setReconcileData] = useState(null); // dry-run response
    const [reconcileBusy, setReconcileBusy] = useState(false);

    useEffect(() => {
        if (user && user.role !== ROLES.KAAFI_ADMIN) {
            navigate("/forbidden", { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        Promise.all([
            api.get("/permissions/users"),
            api.get("/permissions/catalog"),
            api.get("/modules"),
        ])
            .then(([u, c, m]) => {
                setUsers(u.data.filter((x) => !x.deleted_at));
                setCatalog(c.data.items || []);
                setModules(m.data || []);
            })
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const loadForUser = (uid) => {
        if (!uid) {
            setGrants([]); setEffective([]); return;
        }
        setLoading(true);
        Promise.all([
            api.get(`/permissions/user/${uid}`),
            api.get(`/permissions/effective/${uid}`),
        ])
            .then(([g, e]) => {
                setGrants(g.data.grants || []);
                setEffective(e.data.items || []);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadForUser(selectedUserId); }, [selectedUserId]);

    const groups = useMemo(() => {
        const out = [];
        const seen = new Set();
        for (const it of catalog) {
            if (!seen.has(it.group)) { seen.add(it.group); out.push(it.group); }
        }
        return out;
    }, [catalog]);

    const grantUser = users.find((u) => u.id === selectedUserId);

    // Iter14 — Conflict map: for the currently selected user, group active grants
    // by permission_key. A `key` is "conflicted" when it has BOTH active allow(s)
    // AND active deny(s). We use this to (a) badge rows in the overrides table
    // and (b) show a yellow warning banner in the Grant dialog.
    const conflictsByKey = useMemo(() => {
        const map = {};
        for (const g of grants) {
            const bucket = map[g.permission_key] || { allow: [], deny: [] };
            (g.effect === "allow" ? bucket.allow : bucket.deny).push(g);
            map[g.permission_key] = bucket;
        }
        return map;
    }, [grants]);

    const isConflicted = (key) => {
        const b = conflictsByKey[key];
        return !!(b && b.allow.length > 0 && b.deny.length > 0);
    };

    // Existing active DENY(s) for the permission_key currently selected in the
    // Grant dialog. Drives the yellow "Heads up — this DENY will block your new
    // ALLOW" banner.
    const activeDenyForGKey = useMemo(() => {
        if (!gKey) return null;
        const b = conflictsByKey[gKey];
        if (!b) return null;
        return b.deny[0] || null;
    }, [gKey, conflictsByKey]);

    const revokeInline = async (id, reason) => {
        try {
            await api.post(`/permissions/${id}/revoke`, { reason: reason || "revoked_from_grant_dialog_warning" });
            toast.success("Existing deny revoked. Re-open Grant to add the ALLOW.");
            setOpenGrant(false);
            loadForUser(selectedUserId);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const openReconcile = async () => {
        setReconcileBusy(true);
        try {
            const { data } = await api.post("/admin/permission-overrides/reconcile-conflicts?dry_run=true");
            setReconcileData(data);
            setReconcileOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReconcileBusy(false);
        }
    };

    const executeReconcile = async () => {
        setReconcileBusy(true);
        try {
            const { data } = await api.post("/admin/permission-overrides/reconcile-conflicts?dry_run=false");
            toast.success(`Reconciled ${data.resolved} conflict${data.resolved === 1 ? "" : "s"}.`);
            setReconcileOpen(false);
            setReconcileData(null);
            loadForUser(selectedUserId);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReconcileBusy(false);
        }
    };

    const onGrant = async () => {
        if (!selectedUserId || !gKey || !gReason.trim()) {
            toast.error("User, permission key, and reason are required.");
            return;
        }
        try {
            await api.post("/permissions", {
                user_id: selectedUserId,
                permission_key: gKey,
                effect: gEffect,
                scope: gScope,
                module_id: gScope === "module" ? gModule : null,
                expires_at: gExpires ? new Date(gExpires).toISOString() : null,
                granted_reason: gReason.trim(),
            });
            toast.success("Override granted");
            setOpenGrant(false);
            setGKey(""); setGEffect("allow"); setGScope("global");
            setGModule(""); setGExpires(""); setGReason("");
            loadForUser(selectedUserId);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onRevoke = async (id) => {
        const reason = window.prompt("Revoke reason (required):", "");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/permissions/${id}/revoke`, { reason: reason.trim() });
            toast.success("Override revoked");
            loadForUser(selectedUserId);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const effectiveFiltered = useMemo(() => {
        const q = effSearch.trim().toLowerCase();
        return effective.filter((it) => {
            if (groupFilter && it.group !== groupFilter) return false;
            if (!q) return true;
            return it.full_key.toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q);
        });
    }, [effective, groupFilter, effSearch]);

    return (
        <AppShell title="Permission Overrides">
            <section className="space-y-6" data-testid="admin-permissions-page">
                <div className="asl-card p-5 space-y-4">
                    <div className="flex items-end gap-4 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                            <Label htmlFor="user-picker" className="text-xs uppercase tracking-wider text-[color:var(--asl-muted)]">User</Label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger id="user-picker" data-testid="permissions-user-picker">
                                    <SelectValue placeholder="Select a user…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id} data-testid={`user-option-${u.email}`}>
                                            {u.name} — {u.email} · {ROLE_LABELS[u.role] || u.role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            onClick={() => setOpenGrant(true)}
                            disabled={!selectedUserId}
                            className="asl-navy-bg text-white"
                            data-testid="open-grant-dialog-btn"
                        >
                            + Grant override
                        </Button>
                        <Button
                            variant="outline"
                            onClick={openReconcile}
                            disabled={reconcileBusy}
                            className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)] hover:bg-[color:var(--asl-crimson-50,#fdecef)]"
                            data-testid="reconcile-conflicts-btn"
                            title="Scan every user for conflicting ALLOW+DENY overrides on the same permission key"
                        >
                            <Wrench className="h-4 w-4 mr-1" /> Reconcile conflicts…
                        </Button>
                    </div>
                    {grantUser && (
                        <div className="text-sm text-[color:var(--asl-muted)]">
                            Role: <span className="font-semibold asl-navy-text">{ROLE_LABELS[grantUser.role] || grantUser.role}</span>
                            {grantUser.module_scope_code ? <> · Module scope: <span className="mono asl-crimson-text">{grantUser.module_scope_code}</span></> : null}
                        </div>
                    )}
                </div>

                {selectedUserId && (
                    <Tabs defaultValue="overrides">
                        <TabsList>
                            <TabsTrigger value="overrides" data-testid="tab-overrides">Active Overrides ({grants.length})</TabsTrigger>
                            <TabsTrigger value="effective" data-testid="tab-effective">Effective permissions</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overrides" className="mt-4">
                            <div className="asl-card overflow-hidden">
                                <table className="asl-table asl-table--zebra" data-testid="overrides-table">
                                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                           style={{ background: "var(--asl-navy-50)" }}>
                                        <tr>
                                            <th className="text-left px-4 py-3">Permission</th>
                                            <th className="text-left px-4 py-3">Effect</th>
                                            <th className="text-left px-4 py-3">Scope</th>
                                            <th className="text-left px-4 py-3">Expires</th>
                                            <th className="text-left px-4 py-3">Reason</th>
                                            <th className="text-left px-4 py-3">Granted</th>
                                            <th className="px-4 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grants.length === 0 && (
                                            <tr><td colSpan={7} className="text-center px-4 py-8 text-[color:var(--asl-muted)]">No overrides.</td></tr>
                                        )}
                                        {grants.map((g) => (
                                            <tr key={g.id} {...rowOpenProps(navigate, () => setDetail(g), { testid: `override-row-${g.id}`, className: 'border-t' })} style={{ borderColor: "var(--asl-border)" }}>
                                                <td className="px-4 py-3 mono text-[12px]">
                                                    <div className="flex items-center gap-2">
                                                        <span>{g.permission_key}</span>
                                                        {isConflicted(g.permission_key) && (
                                                            <span
                                                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                                                                title={g.effect === "deny"
                                                                    ? "Conflict: this DENY is blocking an active ALLOW override for the same key. Revoke it to activate the ALLOW."
                                                                    : "Conflict: another DENY override on the same key is blocking this ALLOW. Revoke the DENY to activate this."}
                                                                data-testid={`conflict-badge-${g.id}`}
                                                            >
                                                                <AlertTriangle className="h-3 w-3" /> Conflict
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge className={g.effect === "allow" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                                                        {g.effect}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-xs">
                                                    {g.scope === "global" ? "global" : `module: ${modules.find((m)=>m.id===g.module_id)?.code || "—"}`}
                                                </td>
                                                <td className="px-4 py-3 text-xs">{formatExp(g.expires_at)}</td>
                                                <td className="px-4 py-3 text-xs max-w-[260px] truncate" title={g.granted_reason}>{g.granted_reason}</td>
                                                <td className="px-4 py-3 text-xs">{formatExp(g.granted_at)}</td>
                                                <td className="px-4 py-3 text-right" data-no-row-click>
                                                    <Button size="sm" variant="outline" onClick={() => onRevoke(g.id)} data-testid={`revoke-${g.id}`}>
                                                        Revoke
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </TabsContent>

                        <TabsContent value="effective" className="mt-4 space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                                <Input
                                    placeholder="Search permissions…"
                                    value={effSearch}
                                    onChange={(e) => setEffSearch(e.target.value)}
                                    className="max-w-xs"
                                    data-testid="effective-search"
                                />
                                <Select value={groupFilter || "all"} onValueChange={(v) => setGroupFilter(v === "all" ? "" : v)}>
                                    <SelectTrigger className="w-48" data-testid="effective-group-filter">
                                        <SelectValue placeholder="All entities" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All entities</SelectItem>
                                        {groups.map((g) => (
                                            <SelectItem key={g} value={g}>{g}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-xs text-[color:var(--asl-muted)]">{effectiveFiltered.length} / {effective.length}</span>
                            </div>
                            <div className="asl-card overflow-hidden">
                                <table className="asl-table asl-table--zebra" data-testid="effective-table">
                                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                           style={{ background: "var(--asl-navy-50)" }}>
                                        <tr>
                                            <th className="text-left px-4 py-3">Permission</th>
                                            <th className="text-left px-4 py-3">Description</th>
                                            <th className="text-left px-4 py-3">Status</th>
                                            <th className="text-left px-4 py-3">Detail</th>
                                            <th className="px-4 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {effectiveFiltered.map((it) => {
                                            const b = EFFECT_BADGE[it.source] || EFFECT_BADGE.not_granted;
                                            return (
                                                <tr key={it.full_key} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`effective-row-${it.full_key}`}>
                                                    <td className="px-4 py-3 mono text-[12px]">{it.full_key}</td>
                                                    <td className="px-4 py-3 text-xs text-[color:var(--asl-muted)] max-w-md">{it.description}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge className={b.className}>{b.label}</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-[color:var(--asl-muted)]">
                                                        {it.source === "role" ? (
                                                            it.detail?.redundant_override ? (
                                                                <span className="inline-flex items-center gap-1 text-amber-700" title="This user also has a redundant override for this key; role already grants it globally.">
                                                                    <span>—</span>
                                                                    <span className="text-[10px] px-1 py-0.5 rounded bg-amber-50 border border-amber-200">redundant override</span>
                                                                </span>
                                                            ) : "—"
                                                        ) : it.detail && it.detail.scope ? (
                                                            <>
                                                                {it.detail.scope === "module" ? `module: ${modules.find((m)=>m.id===it.detail.module_id)?.code || "—"}` : "global"}
                                                                {it.detail.expires_at ? ` · until ${formatExp(it.detail.expires_at)}` : ""}
                                                            </>
                                                        ) : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-right" data-no-row-click>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => { setGKey(it.full_key); setOpenGrant(true); }}
                                                            data-testid={`quick-grant-${it.full_key}`}
                                                        >
                                                            Grant…
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </TabsContent>
                    </Tabs>
                )}

                <Dialog open={openGrant} onOpenChange={setOpenGrant}>
                    <DialogContent data-testid="grant-dialog">
                        <DialogHeader>
                            <DialogTitle>Grant permission override</DialogTitle>
                        </DialogHeader>
                        {/* Iter14 — Conflict warning banner: shown when the currently
                            picked permission key already has an active DENY on this user. */}
                        {activeDenyForGKey && gEffect === "allow" && (
                            <div
                                className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
                                data-testid="grant-deny-conflict-banner"
                            >
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                                    <div className="text-xs text-amber-900 leading-relaxed">
                                        <strong>Heads up — this permission is currently DENIED for this user at scope: {activeDenyForGKey.scope === "module" ? `module: ${modules.find((m)=>m.id===activeDenyForGKey.module_id)?.code || "—"}` : "global"}.</strong>
                                        <br />
                                        A new ALLOW here will NOT override that deny. Revoke the existing deny first, or your grant will have no effect.
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-amber-500 text-amber-800 hover:bg-amber-100"
                                        onClick={() => revokeInline(activeDenyForGKey.id, "Revoked from grant dialog to make room for a new ALLOW.")}
                                        data-testid="grant-revoke-existing-deny"
                                    >
                                        <ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke existing deny
                                    </Button>
                                </div>
                            </div>
                        )}
                        <div className="space-y-3">
                            <div>
                                <Label>Permission key</Label>
                                <Select value={gKey} onValueChange={setGKey}>
                                    <SelectTrigger data-testid="grant-key-picker"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                    <SelectContent>
                                        {groups.map((grp) => (
                                            <React.Fragment key={grp}>
                                                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] bg-gray-50">{grp}</div>
                                                {catalog.filter((c) => c.group === grp).map((c) => (
                                                    <SelectItem key={c.full_key} value={c.full_key}>{c.full_key} — {c.description}</SelectItem>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Effect</Label>
                                    <Select value={gEffect} onValueChange={setGEffect}>
                                        <SelectTrigger data-testid="grant-effect-picker"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="allow">allow</SelectItem>
                                            <SelectItem value="deny">deny</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Scope</Label>
                                    <Select value={gScope} onValueChange={setGScope}>
                                        <SelectTrigger data-testid="grant-scope-picker"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="global">global</SelectItem>
                                            <SelectItem value="module">module</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {gScope === "module" && (
                                <div>
                                    <Label>Module</Label>
                                    <Select value={gModule} onValueChange={setGModule}>
                                        <SelectTrigger data-testid="grant-module-picker"><SelectValue placeholder="Pick module…" /></SelectTrigger>
                                        <SelectContent>
                                            {modules.map((m) => (
                                                <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div>
                                <Label>Expires (optional)</Label>
                                <DateInput value={gExpires} onChange={setGExpires} data-testid="grant-expires" />
                            </div>
                            <div>
                                <Label>Reason <span className="text-rose-500">*</span></Label>
                                <Input value={gReason} onChange={(e) => setGReason(e.target.value)} placeholder="Why is this override needed?" data-testid="grant-reason" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpenGrant(false)} data-testid="grant-cancel">Cancel</Button>
                            <Button className="asl-navy-bg text-white" onClick={onGrant} data-testid="grant-submit">Save grant</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                {/* Iter14 — Reconcile conflicts dialog. Dry-run first; execute on confirm. */}
                <Dialog open={reconcileOpen} onOpenChange={(o) => { if (!o) { setReconcileOpen(false); setReconcileData(null); } }}>
                    <DialogContent className="max-w-3xl" data-testid="reconcile-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 asl-navy-text">
                                <Wrench className="h-5 w-5" /> Reconcile conflicting overrides
                            </DialogTitle>
                            <DialogDescription>
                                We scan every user for permission keys that have BOTH an active ALLOW and DENY.
                                A more specific scope wins; at the same scope the newer grant wins. Losers are
                                soft-revoked with a full audit trail.
                            </DialogDescription>
                        </DialogHeader>
                        {reconcileData && (
                            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                                <div className="grid grid-cols-3 gap-3 text-xs" data-testid="reconcile-summary">
                                    <div className="asl-card p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Conflict pairs</div>
                                        <div className="text-2xl font-semibold asl-navy-text">{reconcileData.conflict_pairs}</div>
                                    </div>
                                    <div className="asl-card p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Auto-resolvable</div>
                                        <div className="text-2xl font-semibold text-emerald-700">{reconcileData.resolved}</div>
                                    </div>
                                    <div className="asl-card p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Manual review</div>
                                        <div className="text-2xl font-semibold text-amber-700">{(reconcileData.manual_review || []).length}</div>
                                    </div>
                                </div>

                                {reconcileData.proposed && reconcileData.proposed.length > 0 && (
                                    <div>
                                        <h4 className="text-xs uppercase tracking-wider text-[color:var(--asl-muted)] mb-2">Proposed revocations</h4>
                                        <div className="asl-card overflow-hidden">
                                            <table className="asl-table text-xs" data-testid="reconcile-proposed-table">
                                                <thead className="text-[10px] uppercase text-[color:var(--asl-muted)]"
                                                       style={{ background: "var(--asl-navy-50)" }}>
                                                    <tr>
                                                        <th className="text-left px-3 py-2">User</th>
                                                        <th className="text-left px-3 py-2">Permission</th>
                                                        <th className="text-left px-3 py-2">Revoke this</th>
                                                        <th className="text-left px-3 py-2">Keeps</th>
                                                        <th className="text-left px-3 py-2">Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reconcileData.proposed.map((p, i) => (
                                                        <tr key={i} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`reconcile-row-${i}`}>
                                                            <td className="px-3 py-2">{p.user.email}<div className="text-[10px] text-[color:var(--asl-muted)]">{ROLE_LABELS[p.user.role] || p.user.role}</div></td>
                                                            <td className="px-3 py-2 mono text-[11px]">{p.permission_key}</td>
                                                            <td className="px-3 py-2">
                                                                <Badge className="bg-rose-50 text-rose-700 border-rose-200">
                                                                    {p.loser_id === p.deny.id ? "deny" : "allow"} · {(p.loser_id === p.deny.id ? p.deny : p.allow).scope === "module"
                                                                        ? `module: ${(p.loser_id === p.deny.id ? p.deny : p.allow).module_code || "—"}`
                                                                        : "global"}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                                                    {p.winner_id === p.allow.id ? "allow" : "deny"} · {(p.winner_id === p.allow.id ? p.allow : p.deny).scope === "module"
                                                                        ? `module: ${(p.winner_id === p.allow.id ? p.allow : p.deny).module_code || "—"}`
                                                                        : "global"}
                                                                </Badge>
                                                            </td>
                                                            <td className="px-3 py-2 text-[10px] text-[color:var(--asl-muted)]">{p.reason}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {(reconcileData.manual_review || []).length > 0 && (
                                    <div>
                                        <h4 className="text-xs uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
                                            <AlertTriangle className="h-3.5 w-3.5" /> Manual review needed
                                        </h4>
                                        <div className="asl-card overflow-hidden border border-amber-200">
                                            <table className="asl-table text-xs" data-testid="reconcile-manual-table">
                                                <thead className="text-[10px] uppercase text-amber-900" style={{ background: "#FEF3C7" }}>
                                                    <tr>
                                                        <th className="text-left px-3 py-2">User</th>
                                                        <th className="text-left px-3 py-2">Permission</th>
                                                        <th className="text-left px-3 py-2">Allow</th>
                                                        <th className="text-left px-3 py-2">Deny</th>
                                                        <th className="text-left px-3 py-2">Why</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reconcileData.manual_review.map((p, i) => (
                                                        <tr key={i} className="border-t" style={{ borderColor: "#FDE68A" }} data-testid={`reconcile-manual-row-${i}`}>
                                                            <td className="px-3 py-2">{p.user.email}</td>
                                                            <td className="px-3 py-2 mono text-[11px]">{p.permission_key}</td>
                                                            <td className="px-3 py-2 text-[10px]">{p.allow.scope}{p.allow.module_code ? ` (${p.allow.module_code})` : ""} · {formatExp(p.allow.granted_at)}</td>
                                                            <td className="px-3 py-2 text-[10px]">{p.deny.scope}{p.deny.module_code ? ` (${p.deny.module_code})` : ""} · {formatExp(p.deny.granted_at)}</td>
                                                            <td className="px-3 py-2 text-[10px] text-[color:var(--asl-muted)]">{p.reason}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="text-[10px] text-amber-900 mt-1">
                                            These pairs need admin eyes. Use the per-user Overrides page to revoke the one you don't want.
                                        </div>
                                    </div>
                                )}

                                {reconcileData.conflict_pairs === 0 && (
                                    <div className="text-center py-8 text-sm text-[color:var(--asl-muted)]" data-testid="reconcile-empty">
                                        No conflicts detected. Every user's overrides are consistent.
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setReconcileOpen(false); setReconcileData(null); }} data-testid="reconcile-cancel">Cancel</Button>
                            <Button
                                className="asl-crimson-bg text-white hover:brightness-95"
                                disabled={reconcileBusy || !reconcileData || (reconcileData.resolved || 0) === 0}
                                onClick={executeReconcile}
                                data-testid="reconcile-execute"
                            >
                                {reconcileBusy ? "Fixing…" : `Fix them (${(reconcileData?.resolved) || 0})`}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

            </section>
        
            <DetailDialog row={detail} onClose={() => setDetail(null)} title="Permission Grant" testid="detail-detail" />
</AppShell>
    );
}
